import React, { useState } from "react";
import "./index.scss";
import { imageDimensionSet } from "../../helpers";
import Pager from "../Pager/Pager";
import Toggle, { ToggleType } from "../../dynamic/Toggle";

type PlayersProps = {
  title?: string;
  players: Player[];
  sectionLink?: {
    name: string;
    url: string;
    target: "_self" | "_blank";
  };
  limit?: number | null;
};

type Player = {
  firstName: string;
  lastName: string;
  profileImage: string;
  profileImageCustom: string;
  profileLink: string;
  nationality: string;
  playerType?: {
    name: string;
    value: string;
  };
  stats: Stat[];
};

type Stat = {
  heading: string;
  value: string;
  label: string;
};

const Recipients = ({ players, limit = null }: PlayersProps) => (
  <>
    {(limit ? players.slice(0, limit) : players).map(
      (player: Player, key: number) => (
        <div className="player-pathways-recipient" key={key}>
          <div>
            <div className={`player-pathways-recipient__profile-pic`}>
              <img
                src={
                  player.profileImageCustom
                    ? imageDimensionSet({
                        url: player.profileImageCustom,
                        width: 450,
                        height: 560
                      })
                    : imageDimensionSet({
                        url: player.profileImage,
                        width: 300,
                        height: 300
                      })
                }
                className={`player-pathways-recipient__profile-pic-img${
                  player.profileImageCustom ? "" : " default"
                }`}
              />
            </div>
            <div className="player-pathways-recipient__flag">
              <span className={`itf-flags itf-flags--${player.nationality}`} />
            </div>
          </div>
          <div>
            <div className="player-pathways-recipient__name">
              <span className="player-pathways-recipient__name-first">
                {player.firstName}
              </span>
              {player.lastName}
            </div>
            {player.stats.length ? (
              <div className="player-pathways-recipient__stats">
                {player.stats.map((player, key) => (
                  <div key={key} className="player-pathways-recipient__stat">
                    <span className="player-pathways-recipient__stat-heading">
                      {player.heading}
                    </span>
                    <div>
                      <p className="player-pathways-recipient__stat-value">
                        {player.value}
                      </p>
                      <span className="player-pathways-recipient__stat-subheading">
                        {player.label}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="player-pathways-recipient__stats--no-data">
                <p>No data</p>
              </div>
            )}
            <a
              href={player.profileLink}
              className="player-pathways-recipient__profile-link"
            >
              Profile
            </a>
          </div>
        </div>
      )
    )}
  </>
);

export default ({
  title,
  players,
  limit = 5,
  sectionLink = { name: "View Pathway", url: "#", target: "_blank" }
}: PlayersProps) => {
  const defaultToggles = players
    .reduce((arr: ToggleType[], player: Player) => {
      const item = {
        name:
          player.playerType && player.playerType.name
            ? player.playerType.name
            : "",
        value:
          player.playerType && player.playerType.value
            ? player.playerType.value
            : ""
      };
      if (item.value && arr.every(obj => obj.value !== item.value)) {
        arr.push(item);
      }
      return arr;
    }, [])
    .map((tog, key) => ({ ...tog, key, active: key === 0 }))
    .splice(0, 2);
  const [toggles, updateToggle] = useState<ToggleType[]>(defaultToggles);
  const showToggle = toggles.length === 2;
  const defaultFilteredPlayer = showToggle
    ? players.filter(
        player =>
          player.playerType && player.playerType.value === toggles[0].value
      )
    : [];
  const [filteredPlayers, updatePlayers] = useState<Player[]>(
    defaultFilteredPlayer
  );

  const updatePlayerType = (value: string) => {
    // update toggle state
    updateToggle(toggles.map(tog => ({ ...tog, active: tog.value === value })));

    // update player filter
    updatePlayers(
      players.filter(
        player => player.playerType && player.playerType.value === value
      )
    );
  };

  return (
    <section className="page-section">
      <div className="page-section__inner">
        <div className="player-pathways-recipient__wrapper">
          {title && <h2>{title}</h2>}
          {showToggle ? (
            <Toggle toggles={toggles} updateToggle={updatePlayerType} />
          ) : null}
          <div className="player-pathways-recipients">
            <div className="desktop">
              <Pager
                tileWidth={276}
                fadedEdge
                tileClassName={`player-pathways-recipient`}
                sectionLink={sectionLink}
                triggerUpdate={filteredPlayers}
              >
                <Recipients players={showToggle ? filteredPlayers : players} />
              </Pager>
            </div>
            <div className="mobile">
              <Recipients
                limit={limit}
                players={showToggle ? filteredPlayers : players}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
